import React, { Component } from 'react';
import { Box, Button, Chip, Grid, TextField, Tooltip } from '@mui/material';
import moment from 'moment';
import SimpleDatePicker from '../date-pickers/inline-no-today';
import SimpleAutoCompleteSearch from '../auto-complete/autocomplete-search-api';
import Api from '../../services/api';
import LinkIcon from '@mui/icons-material/Link';
import { t } from './usage-i18n';

// ---------------------------------------------------------------------------
// Barra de filtros das telas de uso: presets de período + datas + API + grupo + usuário.
// Controlado pelo pai: value = { preset, dateStart, dateEnd, api, actionGroup, userId }.
// Datas no formato das páginas de relatório ('YYYY/MM/DD HH:mm:ss').
// ---------------------------------------------------------------------------

export const PRESETS = [
  { id: 'today', fallback: 'Hoje', days: 0 },
  { id: 'yesterday', fallback: 'Ontem', days: 1, single: true },
  { id: '7d', fallback: '7 dias', days: 6 },
  { id: '30d', fallback: '30 dias', days: 29 },
  { id: '90d', fallback: '90 dias', days: 89 },
  { id: 'custom', fallback: 'Personalizado' },
];

export function presetRange(id) {
  const p = PRESETS.find((x) => x.id === id) || PRESETS[2];
  const fmt = 'YYYY/MM/DD HH:mm:ss';
  if (p.single) {
    const d = moment().subtract(p.days, 'days');
    return { dateStart: d.startOf('day').format(fmt), dateEnd: d.endOf('day').format(fmt) };
  }
  return {
    dateStart: moment().subtract(p.days || 0, 'days').startOf('day').format(fmt),
    dateEnd: moment().endOf('day').format(fmt),
  };
}

// Período vindo da URL (?start=YYYY-MM-DD&end=YYYY-MM-DD: links entre abas e o 'Copiar link'); sem os dois, o preset.
export function rangeFromQuery(q, fallbackPreset) {
  const start = q.get('start'); const end = q.get('end');
  const fmt = 'YYYY/MM/DD HH:mm:ss';
  if (start && end && moment(start, 'YYYY-MM-DD', true).isValid() && moment(end, 'YYYY-MM-DD', true).isValid()) {
    return { preset: 'custom', dateStart: moment(start, 'YYYY-MM-DD').startOf('day').format(fmt), dateEnd: moment(end, 'YYYY-MM-DD').endOf('day').format(fmt) };
  }
  return { preset: fallbackPreset, ...presetRange(fallbackPreset) };
}

export default class UsagePeriodFilter extends Component {
  constructor(props) {
    super(props);
    this.state = { copied: false };
    this.copyLink = this.copyLink.bind(this);
    this.onPreset = this.onPreset.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onUser = this.onUser.bind(this);
    this.commitIp = this.commitIp.bind(this);
    this.commitUserId = this.commitUserId.bind(this);
  }

  emit(patch) {
    const next = { ...this.props.value, ...patch };
    if (this.props.onChange) this.props.onChange(next);
  }

  onPreset(id) {
    if (id === 'custom') { this.emit({ preset: 'custom' }); return; }
    this.emit({ preset: id, ...presetRange(id) });
  }

  onChange(stateName, value) {
    const v = value && value.id !== undefined ? value.id : value;
    const patch = { [stateName]: v };
    if (stateName === 'dateStart' || stateName === 'dateEnd') patch.preset = 'custom';
    this.emit(patch);
  }

  // O autocomplete dispara {id: 0} também em eventos internos (reset/blur), não só quando o usuário limpa.
  // Por isso valor vazio NÃO zera o filtro (zerava o usuário vindo da URL e a página seguinte falhava com
  // 'Informe o usuário'). Limpar é pelo × do chip, explícito.
  onUser(stateName, value) {
    if (!(value && value.id)) return;
    this.emit({ userId: value.id, userName: value.name || '' });
  }

  // Objeto estável para o `selected` do autocomplete (ele tem useEffect por identidade; objeto novo a
  // cada render viraria loop). Muda só quando id/nome mudam.
  selectedUser(v) {
    const id = v.userId || 0;
    if (!id) { this.cachedUser = null; return null; }
    const name = v.userName || `#${id}`;
    if (!this.cachedUser || this.cachedUser.id !== id || this.cachedUser.name !== name) this.cachedUser = { id, name };
    return this.cachedUser;
  }

  // Id do usuário digitado (quase sempre se tem o id do suspeito): vira o usuário do filtro sem passar pelo
  // autocomplete. Aplicado no blur/Enter. Vazio não limpa (limpar é pelo × do chip). O nome chega pelas linhas.
  commitUserId(raw) {
    const id = parseInt(String(raw || '').trim(), 10);
    if (!(id > 0) || id === ((this.props.value || {}).userId || 0)) return;
    this.emit({ userId: id, userName: '' });
  }

  // IP: campo sem estado próprio (defaultValue + key), aplicado no blur/Enter para não recarregar a cada tecla.
  commitIp(raw) {
    const ip = (raw || '').trim();
    if (ip !== ((this.props.value || {}).ip || '')) this.emit({ ip });
  }

  // Link da investigação: a aba atual + período, usuário (id), IP, API e os filtros próprios da aba (linkParams).
  // Só ids, datas e nomes de filtro — nome e e-mail de pessoa não vão para a URL.
  copyLink() {
    const v = this.props.value || {};
    const fmt = 'YYYY/MM/DD HH:mm:ss';
    const q = new URLSearchParams();
    q.set('start', moment(v.dateStart, fmt).format('YYYY-MM-DD'));
    q.set('end', moment(v.dateEnd, fmt).format('YYYY-MM-DD'));
    if (v.userId) q.set('userId', v.userId);
    if (v.ip) q.set('ip', v.ip);
    if (v.api) q.set('api', v.api);
    const extra = this.props.linkParams || {};
    Object.keys(extra).forEach((k) => { if (extra[k] !== undefined && extra[k] !== null && extra[k] !== '') q.set(k, extra[k]); });
    const url = `${window.location.origin}${window.location.pathname}?${q.toString()}`;
    const done = () => { this.setState({ copied: true }); clearTimeout(this.copiedTimer); this.copiedTimer = setTimeout(() => this.setState({ copied: false }), 2500); };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done).catch(() => window.prompt('', url));
    else window.prompt('', url);
  }

  componentWillUnmount() { clearTimeout(this.copiedTimer); }

  render() {
    const v = this.props.value || {};
    const { intl, showApi = true, showGroup = true, showUser = true, showIp = true, extra, onApply, applying } = this.props;
    return (
      <Box style={{ padding: '12px 16px', border: '1px solid #e0e0e0', borderRadius: 12, background: '#fff' }}>
        <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 6, marginBottom: 10 }}>
          {PRESETS.map((p) => (
            <Chip key={p.id} label={t(intl, `usage.period.${p.id}`, p.fallback)} size="small" clickable
              color={v.preset === p.id ? 'primary' : 'default'}
              variant={v.preset === p.id ? 'filled' : 'outlined'}
              onClick={() => this.onPreset(p.id)} />
          ))}
          {showUser && v.userId ? <Chip size="small" color="primary" label={`${t(intl, 'usage.filter.user', 'Usuário')}: ${v.userName || `#${v.userId}`}`} onDelete={() => this.emit({ userId: 0, userName: '' })} style={{ marginLeft: 8 }} /> : null}
          {showIp && v.ip ? <Chip size="small" color="secondary" label={`${t(intl, 'usage.filter.ip', 'IP')}: ${v.ip}`} onDelete={() => this.emit({ ip: '' })} style={{ marginLeft: 8 }} /> : null}
          <Box style={{ flex: 1 }} />
          <Tooltip title={t(intl, 'usage.filter.copyLink.help', 'Copia o endereço desta aba com o período e os filtros atuais. Quem abrir o link (com acesso à auditoria) vê o mesmo recorte.')}>
            <Chip size="small" variant="outlined" clickable icon={<LinkIcon style={{ fontSize: 16 }} />} color={this.state.copied ? 'primary' : 'default'}
              label={this.state.copied ? t(intl, 'usage.filter.copyLink.done', 'Copiado!') : t(intl, 'usage.filter.copyLink', 'Copiar link')} onClick={this.copyLink} />
          </Tooltip>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <SimpleDatePicker label={t(intl, 'usage.filter.dateStart', 'Data inicial')} name="dateStart" stateName="dateStart" changeDate={this.onChange} currentDate={v.dateStart} fullWidth={true} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SimpleDatePicker label={t(intl, 'usage.filter.dateEnd', 'Data final')} name="dateEnd" stateName="dateEnd" changeDate={this.onChange} currentDate={v.dateEnd} fullWidth={true} />
          </Grid>
          {showApi && (
            <Grid item xs={12} sm={6} md={2}>
              <TextField select fullWidth size="small" label={t(intl, 'usage.filter.api', 'API')} value={v.api || ''} InputLabelProps={{ shrink: true }} SelectProps={{ native: true }}
                onChange={(e) => this.onChange('api', e.target.value)}>
                <option value="">{t(intl, 'usage.filter.api.all', 'Todas')}</option>
                <option value="2do">2DO</option>
                <option value="reports">Reports</option>
                <option value="calledservice">CalledService</option>
                <option value="2talk">2Talk</option>
                <option value="front">{t(intl, 'usage.filter.api.front', 'Front (telas)')}</option>
              </TextField>
            </Grid>
          )}
          {showGroup && (
            <Grid item xs={12} sm={6} md={2}>
              <TextField select fullWidth size="small" label={t(intl, 'usage.filter.type', 'Tipo')} value={v.actionGroup || ''} InputLabelProps={{ shrink: true }} SelectProps={{ native: true }}
                onChange={(e) => this.onChange('actionGroup', e.target.value)}>
                <option value="">{t(intl, 'usage.filter.type.all', 'Todos')}</option>
                <option value="access">{t(intl, 'usage.filter.type.access', 'Acesso (leitura/escrita/tela)')}</option>
                <option value="auth">{t(intl, 'usage.filter.type.auth', 'Autenticação')}</option>
                <option value="denial">{t(intl, 'usage.filter.type.denial', 'Recusas e bloqueios')}</option>
              </TextField>
            </Grid>
          )}
          {showUser && (
            <Grid item xs={12} sm={6} md={2}>
              <SimpleAutoCompleteSearch label={t(intl, 'usage.filter.user', 'Usuário')} options={[]} stateName="userId" changeSelect={this.onUser} selected={this.selectedUser(v)}
                urlapi={Api.apiurlbase() + 'api/users/search/user'} />
            </Grid>
          )}
          {showUser && (
            <Grid item xs={6} sm={3} md={1}>
              <Tooltip title={t(intl, 'usage.filter.userId.help', 'Id do usuário: mais rápido que o autocomplete quando você já tem o id. Aplica ao sair do campo ou com Enter.')}>
                <TextField key={v.userId || 0} fullWidth size="small" type="number" label={t(intl, 'usage.filter.userId', 'Id')} defaultValue={v.userId || ''} InputLabelProps={{ shrink: true }} placeholder="9125001"
                  onBlur={(e) => this.commitUserId(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.commitUserId(e.target.value); } }} />
              </Tooltip>
            </Grid>
          )}
          {showIp && (
            <Grid item xs={12} sm={6} md={2}>
              <Tooltip title={t(intl, 'usage.filter.ip.help', 'Filtra pelo IP gravado no histórico (igualdade exata). Vale para Atividade, Recusas, Erros e Quem acessou, e continua ativo ao trocar de aba. Aplica ao sair do campo ou com Enter.')}>
                <TextField key={v.ip || ''} fullWidth size="small" label={t(intl, 'usage.filter.ip', 'IP')} defaultValue={v.ip || ''} InputLabelProps={{ shrink: true }} placeholder="181.214.221.17"
                  onBlur={(e) => this.commitIp(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.commitIp(e.target.value); } }} />
              </Tooltip>
            </Grid>
          )}
          {extra}
          {onApply && (
            <Grid item xs={12} sm={6} md={1}>
              <Button fullWidth variant="contained" color="secondary" onClick={onApply} disabled={applying}>{t(intl, 'usage.filter.apply', 'Aplicar')}</Button>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  }
}
